import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ExternalLink,
  FileText,
  Columns3,
  Lock,
  AlertTriangle,
} from "lucide-react";
import { useAuth } from "../../scripts/AuthContext";
import { API_BASE_URL } from "@/lib/apiBase";
import { permalinkForPost } from "@/lib/permalinks";
import { getFullName } from "@/lib/userUtils";
import { plainExcerpt } from "@/lib/markdownPlain";
import { cn } from "@/lib/utils";
import {
  formatCriticalRemaining,
  isPostCriticallyActive,
} from "@/lib/feedPosts";

const previewCache = new Map();

function cacheKey(orgId, postId) {
  return `${orgId}:${postId}`;
}

/**
 * Link-unfurl style preview for a workplace post attachment.
 * Preview fields are viewer-scoped; locked when the viewer cannot access the post.
 */
export default function DmPostAttachmentCard({
  attachment,
  orgId,
  inverse = false,
  compact = false,
}) {
  const { accessToken } = useAuth();
  const postOrgId = attachment?.org_id || orgId;
  const postId = attachment?.post_id;
  const apiLocked = attachment?.accessible === false;
  const [preview, setPreview] = useState(() => {
    if (postId == null || apiLocked) return null;
    return previewCache.get(cacheKey(postOrgId, postId)) || null;
  });
  const [locked, setLocked] = useState(apiLocked);

  useEffect(() => {
    setLocked(apiLocked);
    if (apiLocked) {
      setPreview(null);
      return;
    }
    if (!accessToken || postId == null || !postOrgId) return;
    const key = cacheKey(postOrgId, postId);
    if (previewCache.has(key)) {
      const cached = previewCache.get(key);
      if (cached?.locked) {
        setLocked(true);
        setPreview(null);
      } else {
        setPreview(cached);
        setLocked(false);
      }
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/posts/${postId}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: { org_id: postOrgId },
        });
        const post = res.data;
        const data = {
          title:
            plainExcerpt(post.title, 120) ||
            plainExcerpt(post.content, 80) ||
            `Post #${postId}`,
          excerpt: plainExcerpt(post.content, compact ? 100 : 180),
          author: getFullName(post.user) || null,
          teamName: post.team?.name || null,
          orgName: post.org?.name || null,
          kind:
            post.kanban_status != null || post.kanbanStatus != null
              ? "card"
              : "post",
          deleted: !!(post.deleted_at || post.deletedAt),
          critical: isPostCriticallyActive(post),
          criticalUntil: post.critical_until || post.criticalUntil || null,
        };
        previewCache.set(key, data);
        if (!cancelled) {
          setPreview(data);
          setLocked(false);
        }
      } catch (err) {
        const status = err?.response?.status;
        if (status === 403 || status === 404) {
          previewCache.set(key, { locked: true });
          if (!cancelled) {
            setLocked(true);
            setPreview(null);
          }
          return;
        }
        console.error("Failed to load post preview:", err);
        if (!cancelled) {
          setLocked(false);
          setPreview(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken, postOrgId, postId, compact, apiLocked]);

  if (!attachment || attachment.type !== "post" || postId == null) return null;

  const href = permalinkForPost({ orgId: postOrgId, postId });
  const kind = preview?.kind || attachment.kind || "post";
  const isDeleted = preview?.deleted;
  const teamName = locked ? null : preview?.teamName || null;
  const title = locked
    ? null
    : preview?.title ||
      (attachment.title
        ? plainExcerpt(attachment.title, 120)
        : null);
  const excerpt = locked ? null : preview?.excerpt || null;
  const author = locked ? null : preview?.author || null;
  const isCritical = !locked && Boolean(preview?.critical);
  const criticalRemaining = isCritical
    ? formatCriticalRemaining(preview?.criticalUntil)
    : null;

  return (
    <Link
      to={href}
      className={cn(
        "mt-1.5 block rounded-lg border overflow-hidden text-left transition-colors no-underline",
        inverse
          ? "border-transparent bg-primary-foreground text-foreground shadow-sm hover:bg-primary-foreground/90"
          : "border-border bg-background text-foreground hover:bg-muted/40",
        compact ? "min-w-[200px]" : "min-w-[240px] max-w-sm"
      )}
    >
      <div
        className={cn(
          "flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-medium uppercase tracking-wide border-b",
          inverse
            ? "bg-muted/60 border-border/60 text-muted-foreground"
            : "bg-muted/50 border-border text-muted-foreground"
        )}
      >
        {locked ? (
          <Lock className="h-3 w-3" />
        ) : kind === "card" ? (
          <Columns3 className="h-3 w-3" />
        ) : (
          <FileText className="h-3 w-3" />
        )}
        <span>{locked ? "Private" : kind === "card" ? "Card" : "Post"}</span>
        {isCritical && (
          <>
            <span className="opacity-40">·</span>
            <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-400">
              <AlertTriangle className="h-3 w-3" />
              Critical{criticalRemaining ? ` · ${criticalRemaining}` : ""}
            </span>
          </>
        )}
        {teamName && (
          <>
            <span className="opacity-40">·</span>
            <span className="truncate normal-case tracking-normal font-normal">
              {teamName}
            </span>
          </>
        )}
      </div>
      <div className={cn("px-3 py-2.5 space-y-1", compact && "py-2")}>
        {locked ? (
          <p className="text-sm text-muted-foreground">
            You don’t have access to this content
          </p>
        ) : isDeleted ? (
          <p className="text-sm italic text-muted-foreground">
            This post was deleted
          </p>
        ) : (
          <>
            <p
              className={cn(
                "font-semibold text-foreground leading-snug",
                compact ? "text-sm line-clamp-2" : "text-sm line-clamp-2"
              )}
            >
              {title || `${kind === "card" ? "Card" : "Post"} #${postId}`}
            </p>
            {excerpt ? (
              <p
                className={cn(
                  "text-muted-foreground leading-relaxed",
                  compact ? "text-xs line-clamp-2" : "text-xs line-clamp-3"
                )}
              >
                {excerpt}
              </p>
            ) : !preview ? (
              <p className="text-xs text-muted-foreground">
                Open to view this post
              </p>
            ) : null}
            {author && (
              <p className="text-[11px] text-muted-foreground pt-0.5">
                {author}
              </p>
            )}
          </>
        )}
        <span className="inline-flex items-center gap-1 text-[11px] font-medium text-primary pt-1">
          <ExternalLink className="h-3 w-3" />
          Open post
        </span>
      </div>
    </Link>
  );
}
